// Mock data for Dr. Zakariye Abdi Bade's Academic Website
// Multi-page version with expanded content

export const personalInfo = {
  name: "Dr. Zakariye Abdi Bade",
  shortName: "Dr. Zaki Bade",
  title: "PhD (Sociology)",
  role: "Sociologist | Academic Leader | Researcher | Consultant",
  tagline: "Advancing sociological research, governance, and institutional development in Somaliland and beyond.",
  email: "dr.zaki@dr-zakibade.com",
  phone: "+252 63 4453 948",
  location: "Sha'ab area, Hargeisa, Somaliland",
  heroImage: "/images/19-01-30.jpg",
  deskImage: "/images/18-56-08.jpg",
  profileImage: "/images/IMG_8899.png",
  conferenceImage: "/images/IMG_0337.jpg",
  cvUrl: "/Dr-Zaki-CV.pdf"
};

export const about = {
  intro: `Dr. Zakariye Abdi Bade is an accomplished academic leader with over 10 years of experience in higher education, research, consultancy, and institutional leadership. His work spans across multiple domains, from modernizing institutional operations and developing human resources policies to fostering international collaborations and delivering innovative academic programs.`,
  mission: `As a sociologist, Dr. Bade is deeply committed to understanding and addressing the complex social dynamics that shape communities in Somaliland and the Horn of Africa. His research focuses on governance, state-building, social marginalization, and educational development, with a particular emphasis on bridging traditional practices and modern state structures.`,
  philosophy: `With extensive teaching experience across multiple universities and a proven track record in managing high-impact research projects, Dr. Bade embodies a unique combination of academic rigor and practical expertise. His leadership philosophy centers on evidence-based decision-making, institutional capacity building, and the empowerment of communities through education and research.`,
  languages: [
    { language: "Somali", level: "Native", percentage: 100 },
    { language: "English", level: "Fluent", percentage: 95 },
    { language: "Turkish", level: "Fluent", percentage: 85 },
    { language: "Arabic", level: "Good", percentage: 70 }
  ]
};

export const education = [
  {
    period: "2019-2024",
    institution: "Ankara Yildirim Beyazit University, Turkey",
    degree: "Doctor of Philosophy (PhD), Sociology",
    honors: "Honor Graduate",
    description: "Specialized in migration studies and social dynamics of state-building."
  },
  {
    period: "2016-2018",
    institution: "JKUAT, Kenya",
    degree: "MSc Development Studies",
    description: "Focused on development theories and project management."
  },
  {
    period: "2011-2015",
    institution: "AMOUD University, Borama",
    degree: "BBA, Project Planning and Management",
    description: "Foundation in project coordination and organizational management."
  }
];

export const expertise = [
  {
    id: 1,
    title: "Sociology & Development Studies",
    description: "Expert in sociological research methodologies, social theory, and development studies with focus on governance, state-building, and community dynamics.",
    icon: "Users"
  },
  {
    id: 2,
    title: "Research Methodology",
    description: "Specialized in qualitative and quantitative research design, data collection, and analysis using tools like NVIVO.",
    icon: "Search"
  },
  {
    id: 3,
    title: "Monitoring & Evaluation",
    description: "Extensive experience designing M&E frameworks and delivering actionable insights for development projects.",
    icon: "BarChart3"
  },
  {
    id: 4,
    title: "Governance & Elections",
    description: "Senior expertise in electoral administration, governance systems, and democratic processes.",
    icon: "Vote"
  },
  {
    id: 5,
    title: "HR Policy & Reform",
    description: "Proven track record in developing HR policies and implementing institutional reforms.",
    icon: "Briefcase"
  },
  {
    id: 6,
    title: "Project Management",
    description: "Skilled in project planning, implementation, and coordination from inception to completion.",
    icon: "FolderKanban"
  },
  {
    id: 7,
    title: "Data Analytics",
    description: "Proficient in data analysis and interpretation for evidence-based decision making.",
    icon: "LineChart"
  },
  {
    id: 8,
    title: "Academic Writing",
    description: "Published researcher with multiple peer-reviewed publications and thesis supervision experience.",
    icon: "GraduationCap"
  }
];

export const experience = [
  {
    id: 1,
    role: "Director General",
    organization: "SL National Electoral Commission (NEC)",
    period: "Dec 2024 - Present",
    type: "Leadership",
    responsibilities: [
      "Oversee NEC operations and strategic planning for national elections",
      "Lead NEC Post Election Evaluation Report as principal author",
      "Developed key NEC policies: HR Policy, Corporate Communication, Voter Education, Finance Policy, Training Policy, Supply Chain Policy (2025)",
      "Lead stakeholder engagement with government and international partners",
      "Develop election operation plans and risk mitigation strategies"
    ],
    highlights: "Leading national electoral operations & policy development"
  },
  {
    id: 2,
    role: "Advisor",
    organization: "Somaliland Ministry of Foreign Affairs and International Cooperation",
    period: "Dec 2024 - June 2025",
    type: "Advisory",
    responsibilities: [
      "Institutional Capacity Assessment (Management and Functional Review)",
      "Development of National Diaspora Policy",
      "Revision of Communication Policy"
    ],
    highlights: "Strategic policy development for MoFA"
  },
  {
    id: 3,
    role: "BRIDGE Certified Facilitator",
    organization: "BRIDGE International (Building Resources in Democracy, Governance and Elections)",
    period: "Sep 2025",
    type: "Certification",
    responsibilities: [
      "Completed comprehensive BRIDGE International Training to Facilitate (TTF)",
      "Certified to deliver BRIDGE electoral education programs",
      "Training conducted in Nairobi, Kenya"
    ],
    highlights: "International electoral training certification"
  },
  {
    id: 4,
    role: "Director of Human Resources",
    organization: "SL National Electoral Commission (NEC)",
    period: "2021 - 2024",
    type: "Management",
    responsibilities: [
      "Managed 41,000+ staff for voter registration and elections",
      "Led HR policy development and digitalization of operations",
      "Oversaw $3.75M+ budget for staff operations",
      "Drove training and professional development initiatives"
    ],
    highlights: "Managed 41K+ staff, $3.75M budget"
  },
  {
    id: 5,
    role: "Senior Lecturer & Thesis Supervisor",
    organization: "University of Hargeisa, Admas, CSI, University of Burao",
    period: "2021 - Present",
    type: "Academic",
    responsibilities: [
      "Delivered 40+ postgraduate classes in Research Methodology and M&E",
      "Supervised 70+ postgraduate theses since 2021",
      "Teaches Development Theories and Sociology of Education",
      "Introduced innovative teaching strategies across 4 universities"
    ],
    highlights: "70+ theses supervised, 40+ classes delivered"
  },
  {
    id: 6,
    role: "Lead Consultant - SDF Qualitative Surveys",
    organization: "Somaliland Development Fund",
    period: "2023 - 2024",
    type: "Research",
    responsibilities: [
      "Led qualitative survey design and implementation (2023 & 2024)",
      "Developed data collection tools for community governance insights",
      "Conducted Advanced MEAL Training (2023 - delivered twice)"
    ],
    highlights: "Two consecutive years of survey leadership"
  },
  {
    id: 7,
    role: "Lead Researcher - Tuna Value Chain Study",
    organization: "Success Institute for Human Development (SIHD)",
    period: "2023",
    type: "Research",
    responsibilities: [
      "Led comprehensive research on Tuna Fish Value Chain in Somaliland",
      "Analyzed fishing industry infrastructure and market dynamics",
      "Developed recommendations for sustainable fishing practices",
      "Studied gender dynamics and economic contributions of fisheries sector"
    ],
    highlights: "Major fisheries sector research"
  },
  {
    id: 8,
    role: "Research Assistant - Immigration Studies",
    organization: "Ankara Yildirim Beyazit University",
    period: "2019 - 2021",
    type: "Research",
    responsibilities: [
      "Conducted research on migration trends and asylum policy",
      "Collaborated on data collection at Global Migration Center"
    ],
    highlights: "International research experience"
  },
  {
    id: 9,
    role: "MEAL Coordinator",
    organization: "BRCIS Consortium",
    period: "2017 - 2018",
    type: "Development",
    responsibilities: [
      "Led MEAL activities for community development projects",
      "Developed M&E frameworks to track project impact",
      "Provided training on MEAL best practices"
    ],
    highlights: "Built M&E frameworks"
  },
  {
    id: 10,
    role: "Project Coordinator",
    organization: "KAALO Aid & Development Organization",
    period: "2015 - 2016",
    type: "Development",
    responsibilities: [
      "Coordinated development projects from inception to completion",
      "Led project planning and stakeholder engagement"
    ],
    highlights: "End-to-end project coordination"
  }
];

export const publications = {
  peerReviewed: [
    {
      id: 1,
      title: "Urban Land Governance in a Post-Conflict City: Institutional Tensions and Tenure Insecurity in Hargeisa, Somaliland",
      journal: "World Journal of Advanced Research and Reviews",
      year: "2025",
      doi: "https://scholar.google.com.tr/citations?view_op=view_citation&citation_for_view=SMCGKhoAAAAJ:MXK_kJrjxJIC",
      category: "Governance",
      volume: "28",
      issue: "3",
      pages: "2191-2199"
    },
    {
      id: 2,
      title: "Dialect of Traditional Authority and Post-Colonial Modern State: The Case of Somaliland",
      journal: "European Journal of Theoretical and Applied Sciences",
      year: "2023",
      doi: "https://ejtas.com/index.php/journal/article/view/642/502",
      category: "Governance"
    },
    {
      id: 3,
      title: "Bridging Educational Voids: The Emergence and Significance of Sociology in Somali Higher Education",
      journal: "European Journal of Contemporary Education and E-Learning",
      year: "2023",
      doi: "https://ejceel.com/index.php/journal/article/view/38/25",
      category: "Education"
    },
    {
      id: 4,
      title: "From Clans to Colonials to Contemporary Times: The Shifting Dynamics of Somali Social, Economic, and Cultural Life",
      journal: "International Journal of Social Science and Management Research",
      year: "2023",
      doi: "https://ijssmr.org/uploads2023/ijssmr06_72.pdf",
      category: "Society"
    },
    {
      id: 4,
      title: "Self-Determination and Creation of Somaliland National Identity",
      journal: "Global Scientific Journal",
      year: "2023",
      doi: "https://www.globalscientificjournal.com/researchpaper/Self_determination_and_creation_of_Somaliland_national_identity_a_sociological_analysis_of_school_textbooks.pdf",
      category: "Identity"
    },
    {
      id: 5,
      title: "Between Tradition and Progress: Access Constraints to Higher Education for Marginalized Girls",
      journal: "European Journal of Theoretical and Applied Sciences",
      year: "2023",
      doi: "https://ejtas.com/index.php/journal/article/view/403/330",
      category: "Education"
    },
    {
      id: 6,
      title: "Impact of Clan Leaders on Somaliland State Security",
      journal: "International Journal of Social Science and Management Research",
      year: "2023",
      doi: "https://ijssmr.org/uploads2023/ijssmr06_93.pdf",
      category: "Security"
    },
    {
      id: 7,
      title: "Factors Influencing Khat Consumption Among Youth in Garowe",
      journal: "International Journal of Contemporary Applied Researches",
      year: "2021",
      doi: "https://ijcar.net/assets/01.pdf",
      category: "Health"
    },
    {
      id: 8,
      title: "Dynamics of Religion and Nationalism in Somalia: Allies and Opponents",
      journal: "International Journal of Contemporary Applied Researches",
      year: "2020",
      doi: "https://www.ijcar.net/assets/pdf/Vol7-No8-August2020/4.-DYNAMICS-OF-RELIGION-AND-NATIONALISM-IN-SOMALIA-ALLIES-AND-OPPONENTS.pdf",
      category: "Religion"
    },
    {
      id: 9,
      title: "Understanding the Somali Conflict: Causes, Consequences and Strategies for Peacebuilding",
      journal: "Developing Country Studies (IISTE)",
      year: "2021",
      doi: "https://www.iiste.org/Journals/index.php/DCS/article/view/55822",
      category: "Peace"
    },
    {
      id: 10,
      title: "Hermeneutics and Sociology of Religion: Concepts and Methods",
      journal: "ResearchGate Publication",
      year: "2020",
      doi: "https://www.researchgate.net/publication/346447175",
      category: "Methodology"
    },
    {
      id: 11,
      title: "Understanding the Environmental Issues in Somaliland",
      journal: "International Journal of Creative Research Thoughts",
      year: "2022",
      doi: "https://ijcrt.org/papers/IJCRT22A6287.pdf",
      category: "Environment"
    },
    {
      id: 12,
      title: "Exclusion or Empowerment? Women's Roles in Somaliland's Electoral Politics",
      journal: "European Journal of Arts, Humanities and Social Sciences",
      year: "2023",
      doi: "https://ejahss.com/index.php/journal/article/view/31",
      category: "Gender"
    }
  ],
  upcoming: [
    {
      id: 1,
      title: "Beyond Marginality: Exploring the Root Causes of Gabooye Marginalization in Somaliland",
      status: "Accepted for Publication"
    },
    {
      id: 2,
      title: "Silenced Voices: The Persistent Legacies of Marginalization Among the Gabooye",
      status: "Accepted for Publication"
    },
    {
      id: 3,
      title: "From Margins to Mainstream: Envisioning Social Mobility for Somaliland's Gabooye Community",
      status: "Accepted for Publication"
    }
  ]
};

export const projects = [
  {
    id: 1,
    title: "NEC Policies Development 2025",
    client: "SL National Electoral Commission",
    year: "2025",
    role: "Development Partner",
    description: "Developed comprehensive institutional policies including HR Policy, Corporate Communication, Voter Education, Finance Policy, Training Policy, and Supply Chain Policy.",
    image: "/images/IMG_0167.jpg",
    category: "Policy Development"
  },
  {
    id: 2,
    title: "MoFA Institutional Capacity Assessment",
    client: "Somaliland Ministry of Foreign Affairs and International Cooperation",
    year: "2025",
    role: "Lead Consultant",
    description: "Comprehensive Management and Functional Review including development of National Diaspora Policy and revision of Communication Policy.",
    image: "/images/IMG_0178.jpg",
    category: "Institutional"
  },
  {
    id: 3,
    title: "NEC Post-Election Evaluation Report",
    client: "National Electoral Commission",
    year: "2024",
    role: "Lead Author",
    description: "Comprehensive evaluation of electoral processes with detailed analysis and recommendations for future improvements.",
    image: "/images/IMG_0176.jpg",
    category: "Evaluation"
  },
  {
    id: 4,
    title: "NEC Strategic Plan 2024-2029",
    client: "SL National Electoral Commission",
    year: "2024",
    role: "Partner",
    description: "Developed comprehensive 5-year strategic plan outlining goals and implementation frameworks for electoral excellence.",
    image: "/images/IMG_0167.jpg",
    category: "Strategic Planning"
  },
  {
    id: 5,
    title: "SDF Qualitative Surveys",
    client: "Somaliland Development Fund",
    year: "2023-2024",
    role: "Lead Consultant",
    description: "Led qualitative data collection and surveys for two consecutive years (2023 & 2024), improving community governance insights across Somaliland.",
    image: "/images/IMG_0175.jpg",
    category: "Research"
  },
  {
    id: 6,
    title: "Tuna Fish Value Chain Study",
    client: "Success Institute for Human Development (SIHD)",
    year: "2023",
    role: "Lead Researcher",
    description: "Comprehensive study on Somaliland's tuna fishing industry analyzing value chain, infrastructure, gender dynamics, and market opportunities.",
    image: "/images/IMG_0179.jpg",
    category: "Research",
    pdfUrl: "https://customer-assets.emergentagent.com/job_ed30ae61-ac11-457c-bab4-ee47f3c7267d/artifacts/306995ea_FINAL%20TUNA%20VALUE%20CHAIN%20STUDY.pdf"
  },
  {
    id: 7,
    title: "NIMD Elections Project Evaluation",
    client: "Netherlands Institute for Multiparty Democracy",
    year: "2024",
    role: "Associate Evaluator",
    description: "End-of-project evaluation for Somaliland Elections and Democratization Support Project.",
    image: "/images/IMG_0177.jpg",
    category: "Evaluation"
  },
  {
    id: 8,
    title: "Migration Studies Research",
    client: "Ankara Yildirim Beyazit University",
    year: "2019-2021",
    role: "Research Assistant",
    description: "Research on migration trends, asylum policy, and integration at the Global Migration Center.",
    image: "/images/IMG_0179.jpg",
    category: "Research"
  }
];

export const trainings = [
  {
    id: 1,
    title: "BRIDGE International TTF Complete",
    institution: "BRIDGE International",
    audience: "Electoral Professionals",
    year: "2025",
    focus: "Building Resources in Democracy, Governance and Elections - Training to Facilitate",
    location: "Nairobi, Kenya",
    image: "/images/IMG_0180.jpg"
  },
  {
    id: 2,
    title: "Advanced MEAL Training",
    institution: "Somaliland Development Fund",
    audience: "M&E Professionals",
    year: "2023",
    focus: "Advanced Monitoring, Evaluation, Accountability and Learning (Delivered Twice)",
    image: "/images/IMG_0180.jpg"
  },
  {
    id: 3,
    title: "M&E Training for Government",
    institution: "Civil Service Institute & SDF",
    audience: "M&E Directors of Ministries",
    year: "2023",
    focus: "Monitoring, Evaluation, Data Collection, and Reporting",
    image: "/images/IMG_0180.jpg"
  },
  {
    id: 4,
    title: "Research Methodology Training",
    institution: "Raad Research Center",
    audience: "Researchers and Academics",
    year: "2023-2024",
    focus: "Qualitative and Quantitative Methods",
    image: "/images/18-56-09.jpg"
  },
  {
    id: 5,
    title: "Public Research Education",
    institution: "Astaan TV",
    audience: "General Public",
    year: "2023",
    focus: "Research Methodologies Education",
    image: "/images/IMG_0181.jpg"
  },
  {
    id: 6,
    title: "YEEL Project Evaluations",
    institution: "Success Institute for Human Development",
    audience: "Youth Programs",
    year: "2023",
    focus: "Youth Economic Empowerment Evaluation",
    image: "/images/IMG_8512.jpg"
  },
  {
    id: 7,
    title: "Capacity Assessment Training",
    institution: "VSF Germany",
    audience: "Milk and Meat Processors",
    year: "2022",
    focus: "Business Management and Governance",
    image: "/images/IMG_8513.jpg"
  }
];

export const mediaAppearances = [
  {
    id: 1,
    title: "Research Methodology Training - Astaan TV (Part 1)",
    type: "TV Appearance",
    platform: "Astaan TV",
    year: "2023",
    description: "Educational segment on research methodologies for public awareness",
    youtubeUrl: "https://youtu.be/Qb0tQflANWQ?si=rmJZqIXFjSawTMG4",
    embedId: "Qb0tQflANWQ"
  },
  {
    id: 2,
    title: "Research Methodology Training - Astaan TV (Part 2)",
    type: "TV Appearance",
    platform: "Astaan TV",
    year: "2023",
    description: "Continuation of research education series for general public",
    youtubeUrl: "https://youtu.be/DbGaR48_hTc?si=NvTWk3_Cq8968zwZ",
    embedId: "DbGaR48_hTc"
  },
  {
    id: 3,
    title: "Academic Insights & Research Discussion",
    type: "Educational",
    platform: "YouTube",
    year: "2023",
    description: "Expert discussion on academic research and methodology",
    youtubeUrl: "https://youtu.be/Xe0xeoxkefk?si=Y_93JDlP5-1pugjF",
    embedId: "Xe0xeoxkefk"
  },
  {
    id: 4,
    title: "Governance & Development Analysis",
    type: "Educational",
    platform: "YouTube",
    year: "2023",
    description: "In-depth analysis of governance and development topics",
    youtubeUrl: "https://youtu.be/WjL2iTuag3g?si=kdFl90cV1mjqaq4n",
    embedId: "WjL2iTuag3g"
  },
  {
    id: 5,
    title: "Sociology & Social Research",
    type: "Educational",
    platform: "YouTube",
    year: "2023",
    description: "Exploring sociological perspectives and research methods",
    youtubeUrl: "https://youtu.be/3Hy_Rnu-qAM?si=lbTaJZDIimy8Jkx7",
    embedId: "3Hy_Rnu-qAM"
  },
  {
    id: 6,
    title: "Electoral Systems & Democracy",
    type: "Educational",
    platform: "YouTube",
    year: "2023",
    description: "Discussion on electoral processes and democratic governance",
    youtubeUrl: "https://youtu.be/dJ11PEmnt04?si=FNMvkTd1uQZo9trv",
    embedId: "dJ11PEmnt04"
  },
  {
    id: 7,
    title: "Institutional Development Insights",
    type: "Educational",
    platform: "YouTube",
    year: "2023",
    description: "Expert perspectives on institutional capacity building",
    youtubeUrl: "https://youtu.be/U46jnv_g-3w?si=9LFUfoaInQ2Oanan",
    embedId: "U46jnv_g-3w"
  },
  {
    id: 8,
    title: "Research & Policy Discussion",
    type: "Educational",
    platform: "YouTube",
    year: "2023",
    description: "Analysis of research-driven policy development",
    youtubeUrl: "https://youtu.be/azM2aV4TIwI?si=agk3wibJghk6jjfT",
    embedId: "azM2aV4TIwI"
  }
];

export const socialLinks = {
  youtube: "https://youtube.com/@zakariebade?si=c9EL1gWaTCPef0sE",
  facebook: "https://facebook.com/drzakibade",
  whatsapp: "+252 63 4453 948",
  tiktok: "https://www.tiktok.com/@dr164095"
};

export const consultingServices = [
  {
    id: 1,
    title: "Institutional Capacity Assessment",
    description: "Comprehensive evaluation of organizational strengths and development needs with actionable recommendations.",
    icon: "Building2"
  },
  {
    id: 2,
    title: "HR Policy Development",
    description: "Design and implementation of human resource policies aligned with organizational objectives.",
    icon: "Users"
  },
  {
    id: 3,
    title: "M&E Systems Design",
    description: "Development of monitoring and evaluation frameworks to track performance and measure impact.",
    icon: "BarChart3"
  },
  {
    id: 4,
    title: "Research & Evaluation",
    description: "Rigorous research design, data collection, analysis, and reporting for various stakeholders.",
    icon: "FileSearch"
  },
  {
    id: 5,
    title: "Governance & Election Advisory",
    description: "Strategic advisory for electoral processes and democratic institution building.",
    icon: "Vote"
  },
  {
    id: 6,
    title: "Academic Supervision",
    description: "Thesis supervision, research methodology support, and academic writing coaching.",
    icon: "GraduationCap"
  }
];

export const galleryImages = [
  { src: "/images/IMG_0004.jpg", alt: "Professional Event", category: "events" },
  { src: "/images/IMG_0011.jpg", alt: "Conference", category: "events" },
  { src: "/images/IMG_0012.jpg", alt: "Training Session", category: "training" },
  { src: "/images/IMG_0019.jpg", alt: "Workshop", category: "training" },
  { src: "/images/IMG_0020.jpg", alt: "Meeting", category: "events" },
  { src: "/images/IMG_0021.jpg", alt: "Presentation", category: "events" },
  { src: "/images/IMG_0033.jpg", alt: "Academic Event", category: "academic" },
  { src: "/images/IMG_0036.jpg", alt: "Research Discussion", category: "academic" },
  { src: "/images/IMG_0037.jpg", alt: "Team Meeting", category: "events" },
  { src: "/images/IMG_0038.jpg", alt: "Consultation", category: "consulting" },
  { src: "/images/IMG_0107.jpg", alt: "Electoral Work", category: "electoral" },
  { src: "/images/IMG_0109.jpg", alt: "Official Meeting", category: "electoral" },
  { src: "/images/IMG_0132.jpg", alt: "Community Engagement", category: "community" },
  { src: "/images/IMG_0134.jpg", alt: "Field Work", category: "research" },
  { src: "/images/IMG_8514.jpg", alt: "Training Program", category: "training" },
  { src: "/images/IMG_8515.jpg", alt: "Workshop Session", category: "training" },
  { src: "/images/IMG_8516.jpg", alt: "Academic Discussion", category: "academic" },
  { src: "/images/IMG_8517.jpg", alt: "Research Team", category: "research" },
  { src: "/images/IMG_8533.jpg", alt: "Official Event", category: "events" },
  { src: "/images/IMG_8685.jpg", alt: "Professional Portrait", category: "portrait" },
  { src: "/images/IMG_9180.jpg", alt: "Conference Presentation", category: "academic" },
  { src: "/images/IMG_9675.jpg", alt: "Team Collaboration", category: "events" },
  { src: "/images/IMG_9989.jpg", alt: "Research Event", category: "research" },
  { src: "/images/IMG_9992.jpg", alt: "Electoral Commission", category: "electoral" }
];

export const achievements = [
  { number: "10+", label: "Years Experience" },
  { number: "12+", label: "Publications" },
  { number: "70+", label: "Theses Supervised" },
  { number: "40+", label: "Postgraduate Classes" }
];
